/**
 * GraphQL hooks for the pricing page's subscription/checkout flow, talking to
 * the beancount.io api-gateway via the dedicated browser-only Apollo client.
 *
 * Ported from the original beancount-cms (`useIsLoggedIn`, `useUserProfile`,
 * `useSubscriptionStatus`, `useCreateSubscriptionSession`). Types are
 * hand-written because codegen in this repo points at the local schema, not the
 * beancount.io gateway.
 */

import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { useEffect } from "react";
import { AUTH_TOKEN_KEY } from "@/common/apollo/beancount-client";

// --- User profile ---

const QUERY_USER_PROFILE = gql`
  query UserProfile($userId: String) {
    userProfile(userId: $userId) {
      id
      email
      __typename
    }
  }
`;

export interface UserProfileQuery {
  userProfile: { id: string; email: string; __typename?: string } | null;
}
interface UserProfileQueryVariables {
  userId?: string;
}

export function useUserProfile(userId?: string) {
  const { data, loading, error, refetch } = useQuery<
    UserProfileQuery,
    UserProfileQueryVariables
  >(QUERY_USER_PROFILE, {
    variables: { userId },
    fetchPolicy: "network-only",
  });
  return { data, loading, error, refetch };
}

// --- Login state ---

/**
 * Resolves whether the visitor is logged into beancount.io by asking the
 * api-gateway, never by probing storage: the current dashboard authenticates
 * with the HttpOnly `authSess:beancount.io` cookie, which page JavaScript
 * cannot read but which every same-origin request carries automatically.
 * Gating this query on the legacy localStorage token (as the original
 * beancount-cms did) made every cookie-authenticated user look logged out, so
 * the pricing page could only ever offer "Sign up first" and checkout was
 * unreachable. The auth link still attaches the legacy Bearer token when one
 * exists, and an auth error clears that invalid token.
 */
/** True when a GraphQL error message indicates the auth token is invalid. */
export function isAuthError(message: string): boolean {
  return (
    message.includes("Access denied") ||
    message.includes("Unauthorized") ||
    message.includes("Authentication") ||
    message.includes("UNAUTHENTICATED")
  );
}

export function useIsLoggedIn() {
  const hasToken =
    typeof window !== "undefined" &&
    localStorage.getItem(AUTH_TOKEN_KEY) !== null;

  const { data, loading, error } = useQuery<UserProfileQuery>(
    QUERY_USER_PROFILE,
    {
      fetchPolicy: "network-only",
      errorPolicy: "none",
    },
  );

  // External-system side effect: drop an invalid/expired legacy token so the
  // auth link stops sending a Bearer header the gateway rejects.
  useEffect(() => {
    if (hasToken && error && isAuthError(error.message ?? "")) {
      localStorage.removeItem(AUTH_TOKEN_KEY);
    }
  }, [hasToken, error]);

  // Login state is derived from the query result — no setState in an effect.
  const isLoggedIn = !loading && !error && !!data?.userProfile;
  const isLoading = loading;

  return { isLoggedIn, isLoading, user: data?.userProfile ?? null };
}

// --- Subscription status ---

const QUERY_HAS_ACTIVE_SUBSCRIPTION = gql`
  query HasActiveSubscription {
    subscriptionStatus {
      hasActiveSubscription
    }
  }
`;

interface SubscriptionStatusQuery {
  subscriptionStatus: { hasActiveSubscription: boolean } | null;
}

export function useSubscriptionStatus() {
  const { data, loading, error, refetch } = useQuery<SubscriptionStatusQuery>(
    QUERY_HAS_ACTIVE_SUBSCRIPTION,
  );
  return {
    hasActiveSubscription:
      data?.subscriptionStatus?.hasActiveSubscription ?? false,
    loading,
    error,
    refetch,
  };
}

// --- Create checkout session ---

const MUTATE_CREATE_SUBSCRIPTION_SESSION = gql`
  mutation CreateSubscriptionSession($clientId: String!, $priceId: String!) {
    createSubscriptionSession(clientId: $clientId, priceId: $priceId) {
      success
      sessionId
      sessionUrl
      message
    }
  }
`;

interface CreateSubscriptionSessionMutation {
  createSubscriptionSession: {
    success: boolean;
    sessionId?: string | null;
    sessionUrl?: string | null;
    message?: string | null;
  } | null;
}
interface CreateSubscriptionSessionMutationVariables {
  clientId: string;
  priceId: string;
}

export function useCreateSubscriptionSession() {
  const [createSubscriptionSession, { data, loading, error }] = useMutation<
    CreateSubscriptionSessionMutation,
    CreateSubscriptionSessionMutationVariables
  >(MUTATE_CREATE_SUBSCRIPTION_SESSION);

  return {
    createSubscriptionSession,
    data,
    loading,
    error,
    sessionUrl: data?.createSubscriptionSession?.sessionUrl,
  };
}
